import axios, { AxiosResponse } from 'axios';

export class BaseService {
  private cache: Map<string, { data: any; expiry: number }> = new Map();
  private cacheDuration = 5 * 60 * 1000;

  protected api;
  protected path;

  constructor(path: string) {
    this.path = path;
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // GET request
  protected async Get<T>(pathString: string): Promise<T> {
    const cached = this.cache.get(pathString);
    const now = Date.now();

    if (cached && cached.expiry > now) {
      return cached.data as T; // Return cached data if it's still valid
    }

    try {
      const response: AxiosResponse<T> = await this.api.get(this.path + pathString);
      this.cache.set(pathString, { data: response.data, expiry: now + this.cacheDuration })
      return response.data;
    } catch (error) {
      console.error('API GET request failed:', error);
      throw error;
    }
  }

  // POST request
  protected async Post<T, U>(pathString: string, data: U): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api.post(this.path + pathString, data);
      this.cache.delete(pathString);
      return response.data;
    } catch (error) {
      console.error('API POST request failed:', error);
      throw error;
    }
  }

  // PUT request
  protected async Put<T, U>(pathString: string, data: U): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api.put(this.path + pathString, data, data);
      this.cache.delete(pathString);
      return response.data;
    } catch (error) {
      console.error('API PUT request failed:', error);
      throw error;
    }
  }

  // DELETE request
  protected async Delete<T>(pathString: string): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api.delete(this.path + pathString);
      this.cache.delete(pathString);
      return response.data;
    } catch (error) {
      console.error('API DELETE request failed:', error);
      throw error;
    }
  }

  // Optionally add a method to clear the entire cache
  public clearCache(): void {
    this.cache.clear();
  }
}